<script setup lang="ts">
import type { ReactiveFile } from "~/classes/file/ReactiveFile"
import type { PropertyColor, PropertySize } from "~/classes/generated/interface"

import { PropertyId, PropertySchema } from "~/classes/entity"

const props = defineProps({
  reactiveFile: null,
  propertyId: null,
  size: null,
  color: null,
  icon: { type: Boolean },
  textAlignment: null,
  selected: { type: Boolean },
  multiline: { type: Boolean },
  maxLines: null,
  bg: { type: Boolean },
  diff: null
})

const { viewModePropertySizes, viewModePropertyColors } = useViewMode()
const file = props.reactiveFile.ref()

const property = computed(() => props.reactiveFile.property(props.propertyId))
const editing = props.reactiveFile?.propertyEdit
const center = computed(() => props.textAlignment === "center")
</script>

<template>
  <div
    class="property"
    :class="{
      editing,
      selected,
      center,
      bg,
      [viewModePropertySizes[size].singleLineCss]: !multiline && !editing,
      [viewModePropertyColors[color].bgCss]: bg,
    }"
  >
    <!-- <div class="flex flex-none items-center">
      <component
        :is="fileIcons[propertyId as Property]"
        v-if="icon"
        class="flex-none pl-1 text-text-tertiary -mr-0.5"
      />
    </div> -->
    <div v-if="propertyId === PropertyId.FILE_NAME && reactiveFile.mimeType() && reactiveFile.isLink()" class="flex items-center flex-none ml-0.5 -mr-1">
      <icon-property size="xs" type="external" />
    </div>
    <file-property-editable-text
      v-if="propertyId === PropertyId.FILE_NAME"
      :data-property-id="propertyId"
      :property-id
      :schema="PropertySchema.NonEmptyStringValue"
      :selected
      :size
      :reactive-file
      :color
      :diff
      :max-lines
    />
    <file-property-text-diff
      v-else-if="diff && property && [PropertySchema.NonEmptyStringValue, PropertySchema.AnyStringValue].includes(property.value.type!)"
      :file
      :property-id
      :size
      :color
      :selected
      :diff
      :multiline
      :max-lines
    />
    <file-property-path
      v-else-if="propertyId === PropertyId.FILE_VIRTUAL_PATH"
      :data-property-id="propertyId"
      :file
      :size
      :color
    />
    <file-property-dimensions
      v-else-if="propertyId === PropertyId.FILE_VIRTUAL_DIMENSIONS"
      :file
      :property-id
      :size
      :color
      :selected
      class="px-1.5 py-0.5"
    />
    <file-property-tags
      v-else-if="propertyId === PropertyId.FILE_VIRTUAL_TAGS && reactiveFile.tags().size"
      :file
      :size
    />
    <file-property-time
      v-else-if="property && property.value.type === PropertySchema.UtcTimestampMillisValue"
      type="relative"
      :time="new Date(property.value.utcTimestampMillis)"
      :size
      :property-id
      :color
      :selected
    />
    <file-property-time
      v-else-if="propertyId === PropertyId.FILE_VIRTUAL_ADDED_AT && reactiveFile.addedAt()"
      type="relative"
      :time="reactiveFile.addedAt()!"
      :size
      :property-id
      :color
      :selected
    />
    <file-property-rating
      v-else-if="propertyId === PropertyId.FILE_RATING"
      :file
      :property-id
      :size
      :color
      :selected
    />
    <file-property-text
      v-else
      :file
      :property-id
      :size
      :color
      :selected
      :diff
      :multiline
      :max-lines
    />
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
.property
  @apply w-max-content max-w-full inline-flex items-center rounded-[#{$page-properties-br-overlay}]
  @apply min-w-0 // @abhay: added to shrink due to decal
  &.editing
    @apply bg-$global-blue-accent\!
  &.center
    @apply justify-center
  &:not(.editing)
    &.selected
      @apply bg-$global-blue-primary\!
  &.bg
    @apply backdrop-filter backdrop-blur-[#{$menu-blur-default}]
</style>
